import { TileUtilities, TileWidth, TileRefreshStatus } from "./tiles";
import { Salvattore } from "./salvattore";
import { LocalizationService } from "../../localization/localization";

export class TileBinding {
  /** The width of the tile. Valid values are sourced from the TileWidth type ("x-small" | "small" | "medium" | "large" | "x-large" | "xx-large").  */
  public tileWidth = "@";

  /** The user-visible title of the tile. */
  public tileTitle = "@?";

  /** The template to include as content to the tile. TODO: Why is this present when the tiles support transclusion? */
  public templateUrl = "@?";

  /** True if refreshing should be disabled on the tile. False by default. */
  public disableRefresh = "@?";

  /** True if the tile content is automatically refreshed. */
  public autoRefreshed = "<?";

  /** Callback wired up to the refresh button. If this is left undefined, the refresh button is hidden. The callback signature has no arguments. */
  public onRefresh = "&?";

  /** Object of type TileRefreshStatus to wrap properties used in the sdTileRefreshStatus component. */
  public refreshStatus = "<?";
}

export class TileRefreshStatusBindings {
  /** The object of type TileRefreshStatus supplied by the tile controller.*/
  public refreshStatusObj = "<";
}

export class TileLayoutBindings {
  /** The area identifier for all business intelligence (BI) actions triggered by this component.*/
  public biArea = "@?";
}

/** Controller for Tile Layout. */
export class TileLayoutController {
  public layoutEngine: Salvattore;
  public isLayoutRegistered = false;

  // @ngInject
  constructor(
    private $scope: ng.IScope,
    private $window: ng.IWindowService,
    private $element: HTMLElement[],
    private $log: ng.ILogService
  ) {
    this.layoutEngine = (<any>this.$window).salvattore;
    this.$scope.$on("tileLayout.rescanMediaQueries", () => {
      this.layoutEngine.rescanMediaQueries();
    });
  }
  /** Registers the tile layout with salvattore layout engine. */
  public registerLayout(): void {
    // If the tile layout is registerd, don't register it again.
    if (this.isLayoutRegistered) {
      return;
    }

    if (this.layoutEngine) {
      this.layoutEngine.registerGrid(this.$element[0].firstChild);
      this.layoutEngine.rescanMediaQueries();
      this.isLayoutRegistered = true;
    } else {
      this.$log.error("Layout engine is not instantiated.");
    }
  }
}

/** Controller for tile component. */
export class TileController {
  public tileColumnStyle: string;
  public tileWidth: TileWidth;
  public tileTitle: string;
  public disableRefresh: boolean;
  public onRefresh: () => void;
  public showRefresh: boolean;
  public refreshStatus: TileRefreshStatus;
  public autoRefreshed: boolean;
  public showTitle: boolean;
  public biArea: string;

  /** Component lifecycle events */
  public $onInit(): void {
    this.refreshView();

    if (this.disableRefresh && this.refreshStatus) {
      this.refreshStatus.isRefreshDisabled = true;
    }
  }

  public $onChanges(changesObj: ng.IOnChangesObject): void {
    this.refreshView();
  }

  /** Gets executed when the refresh button is clicked. This will invoke the callback set to tile. */
  public refreshClicked($event?: ng.IAngularEvent): void {
    if ($event) {
      $event.preventDefault();
    }

    if (angular.isFunction(this.onRefresh)) {
      this.onRefresh();
    }
  }

  private refreshView(): void {
    this.tileColumnStyle = TileUtilities.getColumnsWidth(this.tileWidth);
    this.showTitle = !!this.tileTitle;
    this.showRefresh = angular.isFunction(this.onRefresh);
  }
}

export class TileRefreshStatusController {
  public refreshStatusObj: TileRefreshStatus;
  private viewAllText: string;
  private drillInViewText: string;
  private orText: string;
  private showInstructions: boolean;
  // @ngInject
  constructor(private sdLocalizationService: LocalizationService) {
    this.viewAllText = sdLocalizationService.getLocalizedString("ShellCommon", "_TILE_REFRESH_STATUS_VIEW_ALL_");
    this.orText = sdLocalizationService.getLocalizedString("ShellCommon", "_TILE_REFRESH_STATUS_OR_");
    this.drillInViewText = sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_TILE_REFRESH_STATUS_DRILL_IN_VIEW_"
    );
    this.showInstructions =
      this.refreshStatusObj.isViewAll ||
      this.refreshStatusObj.isDrillInView ||
      !this.refreshStatusObj.isRefreshDisabled;
  }

  public getLastRefreshDate(): any {
    return this.refreshStatusObj.lastRefreshDate ? this.refreshStatusObj.lastRefreshDate : "--";
  }

  public getContentSpecificInstruction(): string {
    let text = this.refreshStatusObj.isViewAll
      ? this.viewAllText
      : this.refreshStatusObj.isDrillInView
      ? this.drillInViewText
      : "";
    if (text && !this.refreshStatusObj.isRefreshDisabled) {
      text = this.orText + " " + text;
    }
    return text;
  }
}

export const sdTileLayout: ng.IComponentOptions = {
  bindings: <any>new TileLayoutBindings(),
  transclude: true,
  controller: TileLayoutController,
  template: "<div class='sd-tile-layout' data-columns ng-transclude></div>"
};

export function sdTileGroup(): ng.IDirective {
  return {
    transclude: true,
    replace: true,
    require: "^^sdTileLayout",
    templateUrl: (elem: ng.IAugmentedJQuery, attrs: any) => {
      return attrs.templateUrl || "../ShellCommon/Controls/tileLayout/tileGroup.html";
    },
    scope: { shouldHide: "<?" },
    link: <IDirectiveLinkFn>(
      scope: ng.IScope,
      element: ng.IRootElementService,
      attr: ng.IAttributes,
      $ctrl: TileLayoutController
    ) => {
      if (scope && (<any>scope).shouldHide) {
        element.remove();
      } else {
        if (element) {
          element.addClass("tile-group-flex");
        }
        $ctrl.registerLayout();
      }
    }
  };
}

export function sdTile(): ng.IDirective {
  return {
    transclude: {
      actions: "?sdActionTriggers"
    },
    replace: true,
    scope: {},
    bindToController: <any>new TileBinding(),
    controllerAs: "$ctrl",
    controller: TileController,
    templateUrl: "../ShellCommon/Controls/tileLayout/tile.html"
  };
}

export function sdTileRefreshStatus(): ng.IDirective {
  return {
    replace: true,
    scope: {},
    restrict: "E",
    bindToController: <any>new TileRefreshStatusBindings(),
    controllerAs: "$ctrl",
    controller: TileRefreshStatusController,
    templateUrl: "../ShellCommon/Controls/tileLayout/tileRefreshStatus.html"
  };
}
